import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const DashboardView = ({ showToast, currentUser }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });

  useEffect(() => {
    if (!selectedPeriod) {
      setSummaryData(null);
      setIsLoading(false);
      return;
    }
    let isActive = true;
    setIsLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/summary?period=${selectedPeriod}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
    })
      .then(response => {
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('currentUser');
            window.location.reload();
            throw new Error('Oturum süresi doldu veya yetkiniz yok. Lütfen tekrar giriş yapın.');
          }
          throw new Error('Dashboard özeti yüklenemedi.');
        }
        return response.json();
      })
      .then(data => {
        if (isActive) setSummaryData(data);
      })
      .catch(error => {
        if (isActive) {
          showToast("Dashboard özeti yüklenirken hata: " + error.message, "error");
          setSummaryData(null);
        }
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedPeriod, showToast]);

  const pieChartData = summaryData?.paymentStatusCounts ? {
    labels: ['Tamamen Ödendi', 'Kısmi Ödendi', 'Ödenmedi'],
    datasets: [{
      label: 'Ödeme Durumları (Kayıt Sayısı)',
      data: [summaryData.paymentStatusCounts.tamamlandi, summaryData.paymentStatusCounts.kısmi, summaryData.paymentStatusCounts.odenmedi],
      backgroundColor: ['rgba(75, 192, 192, 0.7)', 'rgba(255, 206, 86, 0.7)', 'rgba(255, 99, 132, 0.7)'],
      borderColor: ['rgba(75, 192, 192, 1)', 'rgba(255, 206, 86, 1)', 'rgba(255, 99, 132, 1)'],
      borderWidth: 1,
    }],
  } : null;

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: `${summaryData?.selectedPeriod || selectedPeriod || ''} Dönemi Ödeme Durumu Dağılımı`, font: { size: 16 } },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.label || '';
            if (label) label += ': ';
            if (context.parsed !== null) label += context.parsed + ' kayıt';
            return label;
          }
        }
      }
    }
  };

  if (isLoading && !summaryData) {
    return (
      <div className="bg-white p-6 rounded-lg shadow flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Dashboard verileri yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow relative">
      {isLoading && summaryData && (
        <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-10">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-2xl font-semibold">Gösterge Paneli</h1>
        <div className="flex items-center">
          <label htmlFor="periodFilterDashboard" className="text-sm font-medium text-gray-700 mr-2 whitespace-nowrap">Dönem Seç:</label>
          <input
            type="month"
            id="periodFilterDashboard"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          />
        </div>
      </div>
      {!summaryData && !isLoading && <p className="text-center text-gray-500 py-10">Seçili dönem için özet veri bulunamadı.</p>}
      {summaryData && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-blue-50 p-4 rounded-lg shadow hover:shadow-md transition-shadow">
              <h3 className="text-lg font-medium text-blue-700">Toplam Borç</h3>
              <p className="text-3xl font-bold text-blue-900">{summaryData.totalBorc} TL</p>
              <p className="text-xs text-blue-600">{summaryData.selectedPeriod} Dönemi</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg shadow hover:shadow-md transition-shadow">
              <h3 className="text-lg font-medium text-green-700">Toplam Ödenen</h3>
              <p className="text-3xl font-bold text-green-900">{summaryData.totalOdenen} TL</p>
              <p className="text-xs text-green-600">{summaryData.selectedPeriod} Dönemi</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg shadow hover:shadow-md transition-shadow">
              <h3 className="text-lg font-medium text-red-700">Toplam Kalan Borç</h3>
              <p className="text-3xl font-bold text-red-900">{summaryData.toplamKalanBorc} TL</p>
              <p className="text-xs text-red-600">{summaryData.selectedPeriod} Dönemi</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {pieChartData && (
              <div className="p-4 border rounded-lg bg-gray-50 shadow">
                <div className="relative h-72 md:h-80">
                  <Pie data={pieChartData} options={pieChartOptions} />
                </div>
              </div>
            )}
            <div>
              <h3 className="text-xl font-semibold mb-3">Sayısal İstatistikler <span className="text-sm font-normal text-gray-500">({summaryData.selectedPeriod})</span></h3>
              <div className="overflow-x-auto bg-white rounded-lg shadow border">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr><td className="px-4 py-3 text-sm text-gray-700">Toplam Aidat Kaydı Sayısı</td><td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{summaryData.toplamKayitSayisi}</td></tr>
                    <tr><td className="px-4 py-3 text-sm text-gray-700">Tamamen Ödeyen Sayısı</td><td className="px-4 py-3 text-sm text-right font-medium text-green-700">{summaryData.odemesiTamamlananSayisi}</td></tr>
                    <tr><td className="px-4 py-3 text-sm text-gray-700">Kısmi Ödeme Yapan Sayısı</td><td className="px-4 py-3 text-sm text-right font-medium text-yellow-700">{summaryData.kismenOdeyenSayisi}</td></tr>
                    <tr><td className="px-4 py-3 text-sm text-gray-700">Hiç Ödeme Yapmayan Sayısı</td><td className="px-4 py-3 text-sm text-right font-medium text-red-700">{summaryData.hicOdemeyenSayisi}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardView;
